import { useState, type FormEvent } from 'react';
import { Button, FormField, InlineNotification, TextInput } from '@dpem/design-system';
import { SsoFailureReasonSchema, type AuthProvider, type SsoFailureReason } from '@dpem/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { useApi } from '@/app/providers/ApiProvider';
import { useAuth } from '@/app/providers/AuthProvider';
import { presentError } from '@/lib/api/errors';
import { queryKeys } from '@/lib/api/query-keys';
import {
  DEFAULT_AUTHENTICATED_PATH,
  PATHS,
  REDIRECT_PARAM,
  safeRedirectTarget,
} from '@/router/paths';
import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';

/**
 * What a failed federated sign-in says.
 *
 * `Record<SsoFailureReason, string>` rather than a lookup with a fallback: the reasons
 * are declared in `@dpem/contracts` so the API's redirect and this table cannot drift,
 * and a reason added there without copy here is a compile error rather than a blank
 * notification.
 *
 * `denied` and `not_provisioned` are deliberately different sentences — one is "we know
 * you and you may not sign in", the other is "nobody has made you an account". Neither
 * names a directory fact the person could not already know about themselves.
 */
const SSO_FAILURE_COPY: Record<SsoFailureReason, string> = {
  cancelled: 'Sign-in with IBM w3id was cancelled. You can try again or use your password.',
  expired: 'That sign-in attempt timed out. Start again from this screen.',
  mismatch: 'That sign-in could not be verified in this browser. Start again from this screen.',
  failed: 'IBM w3id sign-in is unavailable right now. Use your password, or try again shortly.',
  not_provisioned:
    'Your IBM account is not registered on this platform yet. Ask an administrator to create it.',
  denied: 'Your account cannot sign in. Contact an administrator.',
};

/** `?sso=` as a known reason, or null. Anything else came from a hand-edited URL. */
const ssoFailureFrom = (raw: string | null): SsoFailureReason | null => {
  const parsed = SsoFailureReasonSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

/**
 * Sign in.
 *
 * Two decisions worth stating.
 *
 * **The failure message never distinguishes a wrong password from an unknown
 * address.** "No account with that email" is a user-enumeration oracle: it lets
 * anyone test an address list against the platform. The server already returns one
 * undifferentiated `UNAUTHENTICATED`; this screen does not undo that by guessing.
 *
 * **The password field is not validated client-side.** A minimum length is a rule
 * for *choosing* a password, not for typing an existing one, and enforcing it here
 * would refuse to submit a legacy credential that the server would have accepted.
 * Registration enforces the policy; sign in reports what the server says.
 */

export function LoginPage() {
  const { status, login, pending, sessionExpired } = useAuth();
  const api = useApi();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const target = safeRedirectTarget(searchParams.get(REDIRECT_PARAM));
  const ssoFailure = ssoFailureFrom(searchParams.get('sso'));

  /**
   * Which federated buttons to render, asked of the server.
   *
   * Not a build-time flag: the same image runs on a deployment that has w3id configured
   * and one that does not, and a hard-coded button on the second sends people to an
   * endpoint that can only fail. Failure here is silent by design — the password form is
   * the fallback, and an error about a missing button would be noise on the one screen
   * where the person is already trying to do something.
   */
  const providers = useQuery({
    queryKey: queryKeys.session.providers,
    queryFn: () => api.authProviders({}),
    staleTime: Infinity,
    retry: false,
  });

  const federated: readonly AuthProvider[] =
    providers.data?.providers.filter((provider) => provider.enabled) ?? [];

  // Restoring: hold the screen rather than flashing the form at someone who is
  // already signed in and about to be redirected.
  if (status === 'restoring') return null;
  if (status === 'authenticated') return <Navigate to={target} replace />;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFailure(null);
    void login({ email, password }).catch((error: unknown) => {
      const presented = presentError(error);
      setFailure(
        presented.code === 'UNAUTHENTICATED'
          ? 'That email and password do not match an account.'
          : presented.title,
      );
      setPassword('');
    });
  };

  const registerTo =
    target === DEFAULT_AUTHENTICATED_PATH
      ? PATHS.register
      : `${PATHS.register}?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;

  /**
   * Where they were going, carried through the IdP round trip.
   *
   * `target` is already `safeRedirectTarget`'s output — a same-origin path — and the
   * server sanitises it a second time before it goes in the flow row. Encoded either
   * way, so a path containing `&` cannot invent a second parameter.
   */
  const startHref = (startPath: string): string =>
    target === DEFAULT_AUTHENTICATED_PATH
      ? startPath
      : `${startPath}?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;

  return (
    <AuthLayout title="Sign in" subtitle="Use the email address your administrator registered.">
      {sessionExpired && (
        <InlineNotification kind="info" title="You were signed out">
          Your session expired. Sign in again to pick up where you left off.
        </InlineNotification>
      )}

      {ssoFailure !== null && (
        <InlineNotification
          kind={ssoFailure === 'cancelled' ? 'info' : 'error'}
          title={ssoFailure === 'cancelled' ? 'Sign-in cancelled' : 'Could not sign you in'}
        >
          {SSO_FAILURE_COPY[ssoFailure]}
        </InlineNotification>
      )}

      {failure !== null && (
        <InlineNotification kind="error" title="Sign in failed" onClose={() => setFailure(null)}>
          {failure}
        </InlineNotification>
      )}

      {federated.length > 0 && (
        <div className={styles.federated}>
          {federated.map((provider) => (
            // A plain anchor with a real href, so it is a navigation the browser owns:
            // middle-click, "open in new tab" and a follower of the 302 into w3id.
            <a key={provider.id} className={styles.federatedButton} href={startHref(provider.startPath)}>
              {provider.displayName}
            </a>
          ))}
          <p className={styles.separator}>or</p>
        </div>
      )}

      <form className={styles.fields} onSubmit={onSubmit} noValidate>
        <FormField label="Email address" required>
          <TextInput
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>

        <FormField label="Password" required>
          <TextInput
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>

        <Button type="submit" loading={pending} fullWidth>
          Sign in
        </Button>
      </form>

      <p className={styles.footer}>
        No account yet?{' '}
        <Link to={registerTo} className={styles.link}>
          Register
        </Link>
      </p>
    </AuthLayout>
  );
}
